import {
  AtonHost,
  useFetchAtonDetailsQuery,
  useGetServiceDefinitions,
  useGetStations,
} from '@ais-ui/api';
import { useMemo } from 'react';
import { AtonHostDetails, AtonWithRelations } from '../types';
import { useMonitorsWithValues } from './hooks/useMonitorEnrichment';
import { useAtonRelations } from './hooks/useAtonRelations';
import { findMonitorTypes } from './monitorGenerator';
import { useLocalization } from '@ais-ui/locale-management';

// Hook for generating base monitor definitions
const useBaseMonitors = (
  atonWithRelations: AtonWithRelations | null,
  services: any
) => {

    const { messages } = useLocalization();

  return useMemo(() => {
    if (!atonWithRelations) return [];
    return findMonitorTypes(atonWithRelations, services, messages).services; // Pass services here
  }, [atonWithRelations, services, messages]);
};

export const useDetailedHostAton = (
  aton: AtonHost | null,
  services: any
): { data: AtonHostDetails; isLoading: boolean; isError: boolean } => {
  // Data fetching hooks
  const {
    data: atonStations,
    isLoading: isAtonStationsLoading,
    isError: isAtonStationsError,
  } = useGetStations();

  const {
    data: atonServiceDefinitions,
    isLoading: isAtonServiceDefinitionsLoading,
    isError: isAtonServiceDefinitionsError,
  } = useGetServiceDefinitions();

  const {
    data: atonDetails,
    isLoading: isAtonDetailsLoading,
    isError: isAtonDetailsError,
  } = useFetchAtonDetailsQuery(aton?.id || ''); // Handle null aton

  // Build AtoN relations
  const atonWithRelations = useAtonRelations(
    aton,
    services,
    atonStations,
    atonServiceDefinitions
  );

  // Generate base monitor definitions - now pass services
  const baseMonitors = useBaseMonitors(atonWithRelations, services);

  // Enrich monitors with real-time values
  const monitorsWithValues = useMonitorsWithValues(
    baseMonitors,
    atonDetails?.value || []
  );

  // Combine loading and error states
  const isLoading =
    isAtonStationsLoading ||
    isAtonServiceDefinitionsLoading ||
    isAtonDetailsLoading;
  const isError =
    isAtonStationsError || isAtonServiceDefinitionsError || isAtonDetailsError;

  // Build final result
  const detailedAtonData = useMemo(() => {
    if (!atonWithRelations) return null;

    return {
      ...atonWithRelations,
      monitors: monitorsWithValues,
    };
  }, [atonWithRelations, monitorsWithValues]);

  return {
    data: detailedAtonData as AtonHostDetails,
    isLoading,
    isError,
  };
};
